import { Injectable, OnDestroy } from '@angular/core';
import { SearchReport } from '../models/search-report.model';
import { Observable, Subject, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ErrorHandlerService } from './error-handler.service';
import { AppConfigService } from './app-config.service';
import { catchError, retry, switchMap, takeUntil } from 'rxjs/operators';
import { SearchReportQuery, SearchReportQueryBatch } from '../models/search-report-query.model';

@Injectable({
  providedIn: 'root'
})
export class SearchReportService implements OnDestroy {

  private allReportsStatus$: Observable<SearchReport[]>;
  private stopPolling = new Subject();

  constructor(private http: HttpClient, private errorHandler: ErrorHandlerService, private appConfigService: AppConfigService) {
    this.allReportsStatus$ = timer(1, 5000).pipe(
      switchMap(() => http.get<SearchReport[]>(`${this.appConfigService.getConfig().webApi}/api2/SearchReports/status`)),
      retry(),
      takeUntil(this.stopPolling)
    );
  }

  ngOnDestroy() {
    this.stopPolling.next(null);
  }

  public getAllSearchReportStatus(): Observable<SearchReport[]> {
    return this.allReportsStatus$;
  }

  public getSearchReports(): Observable<SearchReport[]> {
    return this.http.get<SearchReport[]>(`${this.appConfigService.getConfig().webApi}/api2/SearchReports`)
      .pipe(catchError(this.errorHandler.handleError<SearchReport[]>(`get Search Reports`)));
  }

  public getSearchReport(reportId: string): Observable<SearchReport> {
    return this.http.get<SearchReport>(`${this.appConfigService.getConfig().webApi}/api2/SearchReports/${reportId}`)
      .pipe(catchError(this.errorHandler.handleError<SearchReport>(`get Search Report Id ${reportId}`)));
  }

  public createSearchReport(searchReport: SearchReport): Observable<SearchReport> {
    return this.http.post<SearchReport>(`${this.appConfigService.getConfig().webApi}/api2/SearchReports`, searchReport)
      .pipe(catchError(this.errorHandler.handleError<SearchReport>(`post Search Report Id ${searchReport.title}`)));
  }

  public updateSearchReport(searchReport: SearchReport): Observable<SearchReport> {
    return this.http.put<SearchReport>(`${this.appConfigService.getConfig().webApi}/api2/SearchReports/${searchReport.searchReportId}`, searchReport)
      .pipe(catchError(this.errorHandler.handleError<SearchReport>(`put Search Report ${searchReport.searchReportId}`)));
  }

  public deleteSearchReport(searchReportId: string): Observable<void> {
    return this.http.delete<void>(`${this.appConfigService.getConfig().webApi}/api2/SearchReports/${searchReportId}`)
      .pipe(catchError(this.errorHandler.handleError<void>(`delete Search Report Id ${searchReportId}`)));
  }

  public addSearchReportQueries(reportQueries: SearchReportQueryBatch): Observable<SearchReportQuery[]> {
    return this.http.post<SearchReportQuery[]>(`${this.appConfigService.getConfig().webApi}/api2/SearchReportQueries`, reportQueries)
      .pipe(catchError(this.errorHandler.handleError<SearchReportQuery[]>('post report queries')));
  }

  public updateSearchReportQuery(searcReportQuery: SearchReportQuery): Observable<SearchReportQuery> {
    return this.http.put<SearchReportQuery>(`${this.appConfigService.getConfig().webApi}/api2/SearchReportQueries/${searcReportQuery.searchReportQueryId}`, searcReportQuery)
      .pipe(catchError(this.errorHandler.handleError<SearchReportQuery>(`put Search Report Query ${searcReportQuery.searchReportQueryId}`)));
  }

  public deleteSearchReportQuery(reportQueryId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.appConfigService.getConfig().webApi}/api2/SearchReportQueries/${reportQueryId}`)
      .pipe(catchError(this.errorHandler.handleError<boolean>(`delete Search Report Query Id ${reportQueryId}`)));
  }
}
